import React, { useState, useRef, useEffect } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faGlobe, faFileAlt, faEnvelope, faPhone, 
  faSms, faWifi, faAddressCard, faDownload, 
  faQrcode, faPalette, faSliders
} from '@fortawesome/free-solid-svg-icons';
import { db } from './firebase';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import Dashboard from './Dashboard';
import ThemeToggle from './ThemeToggle';
import './App.css';

function App() {
  const [category, setCategory] = useState('url');
  const [qrValue, setQrValue] = useState('');
  const [qrSettings, setQrSettings] = useState({
    size: 256,
    bgColor: '#FFFFFF',
    fgColor: '#000000',
    level: 'M',
    includeMargin: false,
  });
  const [saveStatus, setSaveStatus] = useState('');

  // Form states for different categories
  const [formData, setFormData] = useState({
    url: '',
    text: '',
    email: '',
    phone: '',
    sms: '',
    wifi_ssid: '',
    wifi_password: '',
    wifi_encryption: 'WPA',
    vcard_name: '',
    vcard_phone: '',
    vcard_email: '',
    vcard_organization: '',
  });

  const qrRef = useRef();

  const categories = [
    { id: 'url', name: 'URL/Website', icon: faGlobe },
    { id: 'text', name: 'ข้อความ', icon: faFileAlt },
    { id: 'email', name: 'Email', icon: faEnvelope },
    { id: 'phone', name: 'เบอร์โทร', icon: faPhone },
    { id: 'sms', name: 'SMS', icon: faSms },
    { id: 'wifi', name: 'WiFi', icon: faWifi },
    { id: 'vcard', name: 'นามบัตร', icon: faAddressCard },
  ];

  // Track page visits
  useEffect(() => {
    const trackVisit = async () => {
      try {
        await addDoc(collection(db, 'visits'), {
          timestamp: serverTimestamp(),
          userAgent: navigator.userAgent,
          language: navigator.language,
        });
      } catch (error) {
        console.log('Error tracking visit:', error);
      }
    };
    trackVisit();
  }, []);

  const handleCategoryChange = (newCategory) => {
    setCategory(newCategory);
    setQrValue('');
  };

  const handleFormChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleSettingChange = (setting, value) => {
    setQrSettings({ ...qrSettings, [setting]: value });
  };

  const generateQRValue = async () => {
    let value = '';
    switch (category) {
      case 'url':
        value = formData.url;
        break;
      case 'text':
        value = formData.text;
        break;
      case 'email':
        value = `mailto:${formData.email}`;
        break;
      case 'phone':
        value = `tel:${formData.phone}`;
        break;
      case 'sms':
        value = `sms:${formData.sms}`;
        break;
      case 'wifi':
        value = `WIFI:T:${formData.wifi_encryption};S:${formData.wifi_ssid};P:${formData.wifi_password};;`;
        break;
      case 'vcard':
        value = `BEGIN:VCARD\nVERSION:3.0\nFN:${formData.vcard_name}\nTEL:${formData.vcard_phone}\nEMAIL:${formData.vcard_email}\nORG:${formData.vcard_organization}\nEND:VCARD`;
        break;
      default:
        value = '';
    }
    setQrValue(value);

    // Save to Firebase
    if (value) {
      try {
        setSaveStatus('กำลังบันทึก...');
        await addDoc(collection(db, 'qrcodes'), {
          category: category,
          data: value,
          settings: qrSettings,
          timestamp: serverTimestamp(),
        });
        setSaveStatus('✓ บันทึกสำเร็จ');
        setTimeout(() => setSaveStatus(''), 3000);
      } catch (error) {
        console.log('Error saving QR code:', error);
        setSaveStatus('⚠ ไม่สามารถบันทึกได้ (ตรวจสอบ Firebase Config)');
        setTimeout(() => setSaveStatus(''), 5000);
      }
    }
  };

  const downloadQR = () => {
    const svg = qrRef.current.querySelector('svg');
    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    
    canvas.width = qrSettings.size;
    canvas.height = qrSettings.size;

    img.onload = () => {
      ctx.drawImage(img, 0, 0);
      const pngFile = canvas.toDataURL('image/png');
      const downloadLink = document.createElement('a');
      downloadLink.download = 'qrcode.png';
      downloadLink.href = pngFile;
      downloadLink.click();
    };

    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
  };

  const renderCategoryForm = () => {
    switch (category) {
      case 'url':
        return (
          <div className="form-group">
            <label>URL:</label>
            <input
              type="url"
              placeholder="https://example.com"
              value={formData.url}
              onChange={(e) => handleFormChange('url', e.target.value)}
            />
          </div>
        );
      case 'text':
        return (
          <div className="form-group">
            <label>ข้อความ:</label>
            <textarea
              placeholder="ใส่ข้อความที่ต้องการ..."
              value={formData.text}
              onChange={(e) => handleFormChange('text', e.target.value)}
              rows="4"
            />
          </div>
        );
      case 'email':
        return (
          <div className="form-group">
            <label>Email:</label>
            <input
              type="email"
              placeholder="example@email.com"
              value={formData.email}
              onChange={(e) => handleFormChange('email', e.target.value)}
            />
          </div>
        );
      case 'phone':
        return (
          <div className="form-group">
            <label>เบอร์โทรศัพท์:</label>
            <input
              type="tel"
              placeholder="0812345678"
              value={formData.phone}
              onChange={(e) => handleFormChange('phone', e.target.value)}
            />
          </div>
        );
      case 'sms':
        return (
          <div className="form-group">
            <label>เบอร์โทรศัพท์:</label>
            <input
              type="tel"
              placeholder="0812345678"
              value={formData.sms}
              onChange={(e) => handleFormChange('sms', e.target.value)}
            />
          </div>
        );
      case 'wifi':
        return (
          <>
            <div className="form-group">
              <label>ชื่อ WiFi (SSID):</label>
              <input
                type="text"
                placeholder="My WiFi"
                value={formData.wifi_ssid}
                onChange={(e) => handleFormChange('wifi_ssid', e.target.value)}
              />
            </div>
            <div className="form-group">
              <label>รหัสผ่าน:</label>
              <input
                type="text"
                placeholder="password123"
                value={formData.wifi_password}
                onChange={(e) => handleFormChange('wifi_password', e.target.value)}
              />
            </div>
            <div className="form-group">
              <label>ประเภทการเข้ารหัส:</label>
              <select
                value={formData.wifi_encryption}
                onChange={(e) => handleFormChange('wifi_encryption', e.target.value)}
              >
                <option value="WPA">WPA/WPA2</option>
                <option value="WEP">WEP</option>
                <option value="nopass">ไม่มีรหัสผ่าน</option>
              </select>
            </div>
          </>
        );
      case 'vcard':
        return (
          <>
            <div className="form-group">
              <label>ชื่อ-นามสกุล:</label>
              <input
                type="text"
                placeholder="สมชาย ใจดี"
                value={formData.vcard_name}
                onChange={(e) => handleFormChange('vcard_name', e.target.value)}
              />
            </div>
            <div className="form-group">
              <label>เบอร์โทรศัพท์:</label>
              <input
                type="tel"
                placeholder="0812345678"
                value={formData.vcard_phone}
                onChange={(e) => handleFormChange('vcard_phone', e.target.value)}
              />
            </div>
            <div className="form-group">
              <label>Email:</label>
              <input
                type="email"
                placeholder="example@email.com"
                value={formData.vcard_email}
                onChange={(e) => handleFormChange('vcard_email', e.target.value)}
              />
            </div>
            <div className="form-group">
              <label>บริษัท/องค์กร:</label>
              <input
                type="text"
                placeholder="บริษัท ABC จำกัด"
                value={formData.vcard_organization}
                onChange={(e) => handleFormChange('vcard_organization', e.target.value)}
              />
            </div>
          </>
        );
      default:
        return null;
    }
  };

  return (
    <div className="App">
      <ThemeToggle />
      <div className="container">
        <header className="header">
          <h1>
            <FontAwesomeIcon icon={faQrcode} className="header-icon" />
            QR Code Generator
          </h1>
          <p>สร้าง QR Code แบบกำหนดเองได้ตามต้องการ </p>
          <div className="header-features">
            <span className="feature-badge">✨ สร้าง QR Code ฟรี</span>
            <span className="feature-badge">💰 ไม่เสียค่าใช้จ่าย</span>
            <span className="feature-badge">♾️ ไม่มีวันหมดอายุ</span>
          </div>
        </header>

        <div className="main-content">
          <div className="left-panel">
            <div className="card">
              <h2>
                <FontAwesomeIcon icon={faSliders} /> เลือกหมวดหมู่
              </h2>
              <div className="category-grid">
                {categories.map((cat) => (
                  <button
                    key={cat.id}
                    className={`category-btn ${category === cat.id ? 'active' : ''}`}
                    onClick={() => handleCategoryChange(cat.id)}
                  >
                    <FontAwesomeIcon icon={cat.icon} className="category-icon" />
                    <span className="category-name">{cat.name}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="card">
              <h2>
                <FontAwesomeIcon icon={faFileAlt} /> ข้อมูล
              </h2>
              {renderCategoryForm()}
              <button className="generate-btn" onClick={generateQRValue}>
                <FontAwesomeIcon icon={faQrcode} /> สร้าง QR Code
              </button>
              {saveStatus && (
                <div className="save-status">{saveStatus}</div>
              )}
            </div>

            <div className="card">
              <h2>
                <FontAwesomeIcon icon={faPalette} /> ปรับแต่ง QR Code
              </h2>
              <div className="form-group">
                <label>ขนาด: {qrSettings.size}px</label>
                <input
                  type="range"
                  min="128"
                  max="512"
                  value={qrSettings.size}
                  onChange={(e) => handleSettingChange('size', parseInt(e.target.value))}
                />
              </div>
              <div className="form-group">
                <label>สีพื้นหลัง:</label>
                <div className="color-picker">
                  <input
                    type="color"
                    value={qrSettings.bgColor}
                    onChange={(e) => handleSettingChange('bgColor', e.target.value)}
                  />
                  <input
                    type="text"
                    value={qrSettings.bgColor}
                    onChange={(e) => handleSettingChange('bgColor', e.target.value)}
                  />
                </div>
              </div>
              <div className="form-group">
                <label>สี QR Code:</label>
                <div className="color-picker">
                  <input
                    type="color"
                    value={qrSettings.fgColor}
                    onChange={(e) => handleSettingChange('fgColor', e.target.value)}
                  />
                  <input
                    type="text"
                    value={qrSettings.fgColor}
                    onChange={(e) => handleSettingChange('fgColor', e.target.value)}
                  />
                </div>
              </div>
              <div className="form-group">
                <label>ระดับการแก้ไขข้อผิดพลาด:</label>
                <select
                  value={qrSettings.level}
                  onChange={(e) => handleSettingChange('level', e.target.value)}
                >
                  <option value="L">ต่ำ (L) - 7%</option>
                  <option value="M">ปานกลาง (M) - 15%</option>
                  <option value="Q">สูง (Q) - 25%</option>
                  <option value="H">สูงมาก (H) - 30%</option>
                </select>
              </div>
              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={qrSettings.includeMargin}
                    onChange={(e) => handleSettingChange('includeMargin', e.target.checked)}
                  />
                  <span>เพิ่มขอบ (Margin)</span>
                </label>
              </div>
            </div>
          </div>

          <div className="right-panel">
            <div className="card qr-display">
              <h2>QR Code ของคุณ</h2>
              {qrValue ? (
                <>
                  <div className="qr-code-wrapper" ref={qrRef}>
                    <QRCodeSVG
                      value={qrValue}
                      size={qrSettings.size}
                      bgColor={qrSettings.bgColor}
                      fgColor={qrSettings.fgColor}
                      level={qrSettings.level}
                      includeMargin={qrSettings.includeMargin}
                    />
                  </div>
                  <button className="download-btn" onClick={downloadQR}>
                    <FontAwesomeIcon icon={faDownload} /> ดาวน์โหลด QR Code
                  </button>
                  <div className="qr-info">
                    <p><strong>ข้อมูลที่เข้ารหัส:</strong></p>
                    <p className="qr-value">{qrValue}</p>
                  </div>
                </>
              ) : (
                <div className="qr-placeholder">
                  <FontAwesomeIcon icon={faQrcode} className="placeholder-icon" />
                  <p>กรุณาเลือกหมวดหมู่และกรอกข้อมูล</p>
                  <p>จากนั้นกดปุ่ม "สร้าง QR Code"</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer with Dashboard */}
        <footer className="footer">
          <Dashboard />
          <div className="footer-credit">
            <p>Created by <a href="https://commsk.dev" target="_blank" rel="noopener noreferrer">commsk.dev</a></p>
            <p className="footer-year">© {new Date().getFullYear()} All rights reserved</p>
          </div>
        </footer>
      </div>
    </div>
  );
}

export default App;
